import React, { useEffect, useRef, useState } from "react";
import FlightDetails from "./FlightDetails";
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Col,
    Row,
} from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import TextFormInput from "component/Common/TextFormInput";
import { useTranslation } from "react-i18next";
import SimpleSelect from "component/Common/simpleSelect";
import COUNTRIES_JSON from "assets/data/countries.json";
import { useFlightContext } from "hooks/flight/useFlightContext";
import { getPassengers } from "helpers/flightHelper";
import DatePicker from "react-flatpickr";

import { flipDate } from "helpers/common";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const nationalityOptions = COUNTRIES_JSON.map((country) => ({
    value: country.fr,
    label: country.fr,
    id: Number(country.codePhone),
}));

const nationalityCodeOptions = COUNTRIES_JSON.map((country) => ({
    value: country.fr + " ( " + country.codePhone + " ) " || country.fr,
    label: country.fr + " ( " + country.codePhone + " ) " || country.fr,
    id: Number(country.codePhone),
}));

const BookingArea = () => {
    const { control, handleSubmit } = useForm();
    const { setPassengersInfos, airValidateResults } = useFlightContext();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { nrOfAdult, nrOfChild, nrOfInfant, nrOfSenior } = getPassengers(
        airValidateResults?.flight,
    );
    const [activeKeys, setActiveKeys] = useState(
        ["99", ...Array(+nrOfAdult + +nrOfChild + +nrOfSenior + 1).keys()].map((i) =>
            i.toString(),
        ),
    );

    const onSubmit = (data) => {
        setActiveKeys(
            ["99", ...Array(nrOfAdult + nrOfChild + nrOfSenior + 1).keys()].map((i) =>
                i.toString(),
            ),
        );
        if (checkMinorSinglePassenger(data)) {
            toast.error("Minor passenger is not allowed to book alone");
            return;
        }
        setPassengersInfos(data);
        console.log("Booking Data:", data);
        navigate("/flight-payment");
    };

    const is18YearsOld = (dob) => {
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        if (
            today.getMonth() < birthDate.getMonth() ||
            (today.getMonth() === birthDate.getMonth() &&
                today.getDate() < birthDate.getDate())
        ) {
            age--;
        }
        return age >= 18;
    };
    const checkMinorSinglePassenger = (data) => {
        if (airValidateResults?.allowMinorSinglePassenger) return false;
        for (let i = 0; i <= nrOfAdult; i++) {
            const dob = data[`dobAdult${i}`];
            if (dob && is18YearsOld(dob)) {
                return false;
            }
        }
        return true;
    };

    return (
        <>
            <div className="flex flex-col gap-4">
                <div className="">
                    {airValidateResults?.flight
                        ?.flightNeedExchangeDocuments && (
                        <p className="bg-[#FCF8E3] text-black p-2.5 border border-yellow-700 rounded-md">
                            {t("flightExchangeDocumentsRequired")}
                        </p>
                    )}
                </div>
                <FlightDetails />
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col w-full p-3 border border-border-color rounded-lg"
                >
                    <h2 className="text-xl font-semibold text-main-color mb-4">
                        {t("passengersInformation")}
                    </h2>
                    <Accordion
                        alwaysOpen
                        defaultActiveKey="0"
                        className="accordion-icon accordion-bg-light flex flex-col gap-2"
                        id="accordionExample2"
                        onSelect={setActiveKeys}
                        activeKey={activeKeys}
                    >
                        <ClientInformation control={control} />
                        {Array.from({ length: nrOfAdult }).map((_, index) => {
                            return (
                                <PassengerInformation
                                    key={index}
                                    control={control}
                                    type="Adult"
                                    index={index + 1}
                                    hasInfant={nrOfInfant > index}
                                    eventKey={`${index}`}
                                />
                            );
                        })}
                        {Array.from({ length: nrOfChild }).map((_, index) => {
                            return (
                                <PassengerInformation
                                    key={index}
                                    control={control}
                                    type="Child"
                                    index={index + 1}
                                    hasInfant={false}
                                    eventKey={`${+nrOfAdult + index}`}
                                />
                            );
                        })}
                        {Array.from({ length: nrOfSenior }).map((_, index) => {
                            return (
                                <PassengerInformation
                                    key={index}
                                    control={control}
                                    type="Senior"
                                    index={index + 1}
                                    hasInfant={false}
                                    eventKey={`${+nrOfAdult + +nrOfChild + index}`}
                                />
                            );
                        })}
                    </Accordion>
                    <div className="justify-end mt-4 flex gap-2">
                        <button
                            onClick={() => {
                                navigate("/flight-search");
                            }}
                            type="button"
                            className="btn w-[40%] border-main-color border-2 hover:text-main-color  bg-transparent text-main-color"
                        >
                            {t("back")}
                        </button>
                        <button
                            type="submit"
                            className="w-[60%] btn bg-main-color text-white"
                        >
                            {t("proceedToPayment")}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default BookingArea;

const ClientInformation = ({ control }) => {
    const [countries, setCountries] = useState(nationalityOptions);
    const [countriesCode, setCountriesCode] = useState(nationalityCodeOptions);
    const { t, i18n } = useTranslation();
    const { airValidateResults } = useFlightContext();

    const handleCountryChange = (inputValue) => {
        const language = i18n.language;
        const tempContry = COUNTRIES_JSON.map((country) => ({
            value: country[language] || country.en,
            label: country[language] || country.en,
            id: Number(country.codePhone),
        }));
        const filteredCountries = tempContry.filter((country) =>
            country.label.toLowerCase().includes(inputValue.toLowerCase()),
        );
        setCountries(filteredCountries);
    };

    const handleCountryCodeChange = (inputValue) => {
        const language = i18n.language;
        const tempContryCode = COUNTRIES_JSON.map((country) => ({
            value:
                country[language] + " ( " + country.codePhone + " ) " ||
                country.en,
            label:
                country[language] + " ( " + country.codePhone + " ) " ||
                country.en,
            id: Number(country.codePhone),
        }));
        const filteredCountries = tempContryCode.filter((country) =>
            country.label.toLowerCase().includes(inputValue.toLowerCase()),
        );
        setCountriesCode(filteredCountries);
    };

    return (
        <AccordionItem eventKey="99">
            <AccordionHeader className="p-2">
                <div className="">Client Information</div>
            </AccordionHeader>
            <AccordionBody>
                <div className="row">
                    <div className="col-lg-6">
                        <TextFormInput
                            name={"customerMobileNo"}
                            control={control}
                            label={"Mobile No"}
                            rules={{
                                required: t("mobileNumberIsRequired"),
                                pattern: {
                                    value: /^[0-9]{10}$/,
                                    message: t("invalidMobileNumber"),
                                },
                            }}
                            placeholder="Mobile No*"
                        />
                    </div>
                    <div className="col-lg-6">
                        <TextFormInput
                            name="customerEmail"
                            type="email"
                            label={t("emailAddress")}
                            placeholder={t("enterYourEmailAddress")}
                            containerClass="col-md-6"
                            control={control}
                            rules={{
                                required: t("emailAddressIsRequired"),
                                pattern: {
                                    value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                                    message: t("invalidEmailAddress"),
                                },
                            }}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="text-sm text-gray-600">
                            {t("countryCode")}{" "}
                            <span className="text-danger">*</span>{" "}
                        </label>
                        <Controller
                            name={`customerCountryCode`}
                            control={control}
                            rules={{
                                required: t("countryCodeIsRequired"),
                            }}
                            defaultValue={nationalityCodeOptions.find(
                                (option) => option.id === 213,
                            )}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <SimpleSelect
                                        options={countriesCode}
                                        placeholder={t("selectCountry")}
                                        onChange={(x) => {
                                            field.onChange(x);
                                        }}
                                        onInputChange={handleCountryCodeChange}
                                        clearable={true}
                                        dashed={true}
                                        defaultValue={nationalityCodeOptions.find(
                                            (option) => option.id === 213,
                                        )}
                                    />
                                    {error && (
                                        <div className="text-red-500 text-[12px]">
                                            {error.message}
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="text-sm text-gray-600">
                            {t("country")}{" "}
                            <span className="text-danger">*</span>{" "}
                        </label>
                        <Controller
                            name={`customerCountry`}
                            control={control}
                            rules={{
                                required: t("countryIsRequired"),
                            }}
                            defaultValue={nationalityOptions.find(
                                (option) => option.id === 213,
                            )}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <SimpleSelect
                                        options={countries}
                                        placeholder={t("selectCountry")}
                                        onChange={(x) => {
                                            field.onChange(x);
                                        }}
                                        onInputChange={handleCountryChange}
                                        clearable={true}
                                        dashed={true}
                                        defaultValue={nationalityOptions.find(
                                            (option) => option.id === 213,
                                        )}
                                    />
                                    {error && (
                                        <div className="text-red-500 text-[12px]">
                                            {error.message}
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>
                    <div className="col-lg-6">
                        {airValidateResults?.customerAddressMandatory && (
                            <TextFormInput
                                name="customerAddress"
                                type="text"
                                label={t("address")}
                                placeholder={t("enterYourAddress")}
                                containerClass="col-md-6"
                                control={control}
                                rules={{
                                    required: "address required",
                                }}
                            />
                        )}
                        {!airValidateResults?.customerAddressMandatory && (
                            <TextFormInput
                                name="customerAddress"
                                type="text"
                                label={t("address")}
                                placeholder={t("enterYourAddress")}
                                containerClass="col-md-6"
                                control={control}
                                noValidate
                            />
                        )}
                    </div>
                </div>
            </AccordionBody>
        </AccordionItem>
    );
};

const PassengerInformation = ({
    control,
    type,
    index,
    hasInfant,
    eventKey,
}) => {
    const [countries, setCountries] = useState(nationalityOptions);
    const { t, i18n } = useTranslation();
    const { airValidateResults, availabilityResults } = useFlightContext();

    useEffect(() => {
        const newCountries = COUNTRIES_JSON.map((country) => ({
            value: country[i18n.language] || country.fr,
            label: country[i18n.language] || country.fr,
            id: Number(country.codePhone),
        }));
        setCountries(newCountries);
    }, [i18n.language]);

    const handleCountryChange = (inputValue) => {
        const language = i18n.language;
        const tempContry = COUNTRIES_JSON.map((country) => ({
            value: country[language] || country.en,
            label: country[language] || country.en,
            id: Number(country.codePhone),
        }));
        const filteredCountries = tempContry.filter((country) =>
            country.label.toLowerCase().includes(inputValue.toLowerCase()),
        );
        setCountries(filteredCountries);
    };

    const getPassengerName = () => {
        const { language } = i18n;

        const ordinalsEn = [
            "First",
            "Second",
            "Third",
            "Fourth",
            "Fifth",
            "Sixth",
            "Seventh",
            "Eighth",
            "Ninth",
        ];
        const ordinalsFr = [
            "1<sup>er</sup>",
            "2<sup>ème</sup>",
            "3<sup>ème</sup>",
            "4<sup>ème</sup>",
            "5<sup>ème</sup>",
            "6<sup>ème</sup>",
            "7<sup>ème</sup>",
            "8<sup>ème</sup>",
            "9<sup>ème</sup>",
        ];
        const ordinalsAr = [
            "الأول",
            "الثاني",
            "الثالث",
            "الرابع",
            "الخامس",
            "السادس",
            "السابع",
            "الثامن",
            "التاسع",
        ];

        let ordinals;
        switch (language) {
            case "fr":
                ordinals = ordinalsFr;
                break;
            case "ar":
                ordinals = ordinalsAr;
                break;
            case "en":
            default:
                ordinals = ordinalsEn;
                break;
        }

        const ordinalText = ordinals[index - 1] || index;

        if (language === "fr") {
            const frType =
                type.toLowerCase() === "adult" ? "adulte" :
                type.toLowerCase() === "child" ? "enfant" : "sénior";
            return (
                <span
                    dangerouslySetInnerHTML={{
                        __html: `${ordinalText} ${frType}`,
                    }}
                />
            );
        } else if (language === "ar") {
            const arabicType =
                type.toLowerCase() === "adult" ? "البالغ" :
                type.toLowerCase() === "child" ? "الطفل" : "كبير السن";
            return `${arabicType} ${ordinalText}`;
        }

        const enType =
            type.toLowerCase() === "adult" ? "adult" :
            type.toLowerCase() === "child" ? "child" : "senior";
        return `${ordinalText} ${enType}`;
    };

    const getMinDateToExp = () => {
        const flight = airValidateResults?.flight;
        if (flight?.bounds?.length > 1) {
            return flipDate(flight?.bounds[1]?.departure?.full_date);
        }
        return flipDate(flight?.bounds[0]?.departure?.full_date);
    };

    return (
        <AccordionItem eventKey={eventKey} className="border">
            <AccordionHeader className="p-2">
                <div className="">{getPassengerName()}</div>
            </AccordionHeader>
            <AccordionBody>
                <div className="row">
                    <div className="col-lg-2">
                        <label className="text-sm text-gray-600">
                            {t("title")}{" "}
                            <span className="text-danger">*</span>{" "}
                        </label>
                        <Controller
                            name={`title${type}${index}`}
                            control={control}
                            rules={{
                                required: t("titleIsRequired"),
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <SimpleSelect
                                        searchable={false}
                                        options={[
                                            {
                                                value: t("Mr"),
                                                label: t("Mr"),
                                                id: 1,
                                            },
                                            {
                                                value: t("Mrs"),
                                                label: t("Mrs"),
                                                id: 2,
                                            },
                                            {
                                                value: t("Ms"),
                                                label: t("Ms"),
                                                id: 3,
                                            },
                                        ]}
                                        placeholder={t("selectTitle")}
                                        onChange={(x) => field.onChange(x)}
                                        dashed={true}
                                    />
                                    {error && (
                                        <div className="text-red-500 text-[12px]">
                                            {error.message}
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>
                    <div className="col-lg-5">
                        <TextFormInput
                            rules={{
                                required: t("firstNameIsRequired"),
                                pattern: {
                                    value: /^[a-zA-Z\u0621-\u064A\u0660-\u0669\s]*$/,
                                    message: t("invalidName"),
                                },
                                validate: (value) => {
                                    if (value.length < 3)
                                        return t("firstnameMustbe3");
                                    if (
                                        value.length >
                                        Math.floor(
                                            (airValidateResults?.paxNameCharacterLimit ||
                                                30) / 2,
                                        )
                                    )
                                        return `firstname must be less than ${Math.floor(
                                            (airValidateResults?.paxNameCharacterLimit ||
                                                30) / 2,
                                        )} characters`;
                                    return true;
                                },
                            }}
                            label={t("firstName")}
                            control={control}
                            name={`firstName${type}${index}`}
                            placeholder={t("firstName")}
                        />
                    </div>
                    <div className="col-lg-5">
                        <TextFormInput
                            containerClass="col-md-6"
                            rules={{
                                required: t("lastNameIsRequired"),
                                pattern: {
                                    value: /^[a-zA-Z\u0621-\u064A\u0660-\u0669\s]*$/,
                                    message: t("invalidName"),
                                },
                                validate: (value) => {
                                    if (value.length < 3)
                                        return t("lastnameMustbe3");
                                    if (
                                        value.length >
                                        Math.floor(
                                            (airValidateResults?.paxNameCharacterLimit ||
                                                30) / 2,
                                        )
                                    )
                                        return `lastname must be less than ${Math.floor(
                                            (airValidateResults?.paxNameCharacterLimit ||
                                                30) / 2,
                                        )} characters`;
                                    return true;
                                },
                            }}
                            label={t("lastName")}
                            control={control}
                            name={`lastName${type}${index}`}
                            placeholder={t("lastName")}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="text-sm text-gray-600">
                            {t("dateOfBirth")}{" "}
                            <span className="text-danger">*</span>{" "}
                        </label>
                        <DatePickerComponent
                            type={type}
                            index={index}
                            control={control}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="text-sm text-gray-600">
                            {t("nationality")}
                            <span className="text-danger">*</span>{" "}
                        </label>
                        <Controller
                            name={`country${type}${index}`}
                            control={control}
                            rules={{
                                required: t("countryIsRequired"),
                            }}
                            defaultValue={nationalityOptions.find(
                                (option) => option.id === 213,
                            )}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <SimpleSelect
                                        options={countries}
                                        placeholder={t("selectCountry")}
                                        onChange={(x) => {
                                            field.onChange(x);
                                            setCountries(nationalityOptions);
                                        }}
                                        onInputChange={handleCountryChange}
                                        dashed={true}
                                        clearable={true}
                                        defaultValue={nationalityOptions.find(
                                            (option) => option.id === 213,
                                        )}
                                    />
                                    {error && (
                                        <div className="text-red-500 text-[12px]">
                                            {error.message}
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    {availabilityResults?.isDomesticFlight ? (
                        <>
                            <Col md={6}>
                                <label className="text-sm text-gray-600">
                                    {t("identityCard")}
                                </label>
                                <Controller
                                    name={`identitiy${type}${index}`}
                                    control={control}
                                    render={({
                                        field,
                                        fieldState: { error },
                                    }) => (
                                        <>
                                            <SimpleSelect
                                                options={[
                                                    {
                                                        value: t("passport"),
                                                        label: t("passport"),
                                                        id: 1,
                                                    },
                                                    {
                                                        value: t(
                                                            "identityCard",
                                                        ),
                                                        label: t(
                                                            "identityCard",
                                                        ),
                                                        id: 2,
                                                    },
                                                ]}
                                                placeholder={t(
                                                    "selectIdentity",
                                                )}
                                                onChange={(x) =>
                                                    field.onChange(x)
                                                }
                                                dashed={true}
                                            />
                                            {error && (
                                                <div className="text-red-500 text-[12px]">
                                                    {error.message}
                                                </div>
                                            )}
                                        </>
                                    )}
                                />
                            </Col>
                            <Col md={6}>
                                <label className="text-sm text-gray-600">
                                    {t("cardNumber")}
                                </label>
                                <TextFormInput
                                    rules={{
                                        pattern: {
                                            value: /^[0-9]*$/,
                                            message: t("invalidCardNumber"),
                                        },
                                        validate: (value) => {
                                            if (value?.length < 8)
                                                return "card number must be at least 8 numbers";
                                            if (value?.length > 20)
                                                return `card number must be less than 20 numbers`;
                                            return true;
                                        },
                                    }}
                                    control={control}
                                    name={`cardNumber${type}${index}`}
                                    placeholder={t("enterCardNumber")}
                                />
                            </Col>
                            <Col md={6}>
                                <label className="text-sm text-gray-600">
                                    {t("cardExpiry")}
                                </label>
                                <Controller
                                    name={`cardExpiry${type}${index}`}
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <DatePicker
                                                className="form-control flatpickr"
                                                placeholder={t(
                                                    "enterPassportExpiry",
                                                )}
                                                options={{
                                                    altInput: true,
                                                    altFormat: "d M Y",
                                                    dateFormat: "Y-m-d",
                                                    minDate: getMinDateToExp(),
                                                }}
                                                onChange={(date) =>
                                                    field.onChange(date)
                                                }
                                                value={field.value}
                                            />
                                        </>
                                    )}
                                />
                            </Col>
                        </>
                    ) : (
                        <>
                            <Col md={6}>
                                <label className="text-sm text-gray-600">
                                    {t("passportNumber")}{" "}
                                    <span className="text-danger">*</span>
                                </label>
                                <TextFormInput
                                    rules={{
                                        required: t("passportNumberIsRequired"),
                                        pattern: {
                                            value: /^[A-Za-z0-9]{6,9}$/,
                                            message: t("invalidPassportNumber"),
                                        },
                                    }}
                                    control={control}
                                    name={`passportNumber${type}${index}`}
                                    placeholder={t("enterPassportNumber")}
                                />
                            </Col>
                            <Col md={6}>
                                <label className="text-sm text-gray-600">
                                    {t("passportExpiry")}{" "}
                                    <span className="text-danger">*</span>
                                </label>
                                <Controller
                                    rules={{
                                        required: t("passportExpiryIsRequired"),
                                    }}
                                    name={`passportExpiry${type}${index}`}
                                    control={control}
                                    render={({
                                        field,
                                        fieldState: { error },
                                    }) => (
                                        <>
                                            <DatePicker
                                                className="form-control flatpickr"
                                                placeholder={t(
                                                    "enterPassportExpiry",
                                                )}
                                                options={{
                                                    altInput: true,
                                                    altFormat: "d M Y",
                                                    dateFormat: "Y-m-d",
                                                    minDate: getMinDateToExp(),
                                                }}
                                                onChange={(date) =>
                                                    field.onChange(date)
                                                }
                                                value={field.value}
                                            />
                                            <div className="text-red-500 text-[12px]">
                                                {error && error.message}
                                            </div>
                                        </>
                                    )}
                                />
                            </Col>
                        </>
                    )}

                    {airValidateResults?.flight.flightNeedExchangeDocuments && (
                        <>
                            <FileUploadComponent
                                control={control}
                                type={type}
                                index={index}
                                fieldName={`exchangeDocuments${type}${index}`}
                            />
                        </>
                    )}

                    {hasInfant && (
                        <>
                            <h6 className="my-4 font-semibold text-[17px]">
                                Baby {index} Information (-2 years){" "}
                            </h6>
                            <Row className="">
                                <div className="col-lg-2">
                                    <label className="text-sm text-gray-600">
                                        {t("title")}{" "}
                                        <span className="text-danger">
                                            *
                                        </span>{" "}
                                    </label>
                                    <Controller
                                        name={`titleInfant${index}`}
                                        control={control}
                                        rules={{
                                            required: t("titleIsRequired"),
                                        }}
                                        render={({
                                            field,
                                            fieldState: { error },
                                        }) => (
                                            <>
                                                <SimpleSelect
                                                    searchable={false}
                                                    options={[
                                                        {
                                                            value: t("Mr"),
                                                            label: t("Mr"),
                                                            id: 1,
                                                        },
                                                        {
                                                            value: t("Mrs"),
                                                            label: t("Mrs"),
                                                            id: 2,
                                                        },
                                                        {
                                                            value: t("Ms"),
                                                            label: t("Ms"),
                                                            id: 3,
                                                        },
                                                    ]}
                                                    placeholder={t(
                                                        "selectTitle",
                                                    )}
                                                    onChange={(x) =>
                                                        field.onChange(x)
                                                    }
                                                    dashed={true}
                                                />
                                                {error && (
                                                    <div className="text-red-500 text-[12px]">
                                                        {error.message}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="col-lg-5">
                                    <TextFormInput
                                        rules={{
                                            required: t("firstNameIsRequired"),
                                            pattern: {
                                                value: /^[a-zA-Z\u0621-\u064A\u0660-\u0669\s]*$/,
                                                message: t("invalidName"),
                                            },
                                            validate: (value) => {
                                                if (value?.length < 3)
                                                    return t(
                                                        "firstnameMustbe3",
                                                    );
                                                if (
                                                    value?.length >
                                                    Math.floor(
                                                        (airValidateResults?.paxNameCharacterLimit ||
                                                            30) / 2,
                                                    )
                                                )
                                                    return `firstname must be less than ${Math.floor(
                                                        (airValidateResults?.paxNameCharacterLimit ||
                                                            30) / 2,
                                                    )} characters`;
                                                return true;
                                            },
                                        }}
                                        label={t("firstName")}
                                        control={control}
                                        name={`firstNameInfant${index}`}
                                        placeholder={t("firstName")}
                                    />
                                </div>
                                <div className="col-lg-5">
                                    <TextFormInput
                                        containerClass="col-md-6"
                                        rules={{
                                            required: t("lastNameIsRequired"),
                                            pattern: {
                                                value: /^[a-zA-Z\u0621-\u064A\u0660-\u0669\s]*$/,
                                                message: t("invalidName"),
                                            },
                                            validate: (value) => {
                                                if (value?.length < 3)
                                                    return t("lastnameMustbe3");
                                                if (
                                                    value?.length >
                                                    Math.floor(
                                                        (airValidateResults?.paxNameCharacterLimit ||
                                                            30) / 2,
                                                    )
                                                )
                                                    return `lastname must be less than ${Math.floor(
                                                        (airValidateResults?.paxNameCharacterLimit ||
                                                            30) / 2,
                                                    )} characters`;
                                                return true;
                                            },
                                        }}
                                        label={t("lastName")}
                                        control={control}
                                        name={`lastNameInfant${index}`}
                                        placeholder={t("lastName")}
                                    />
                                </div>
                                <Col md={6}>
                                    <label className="text-sm text-gray-600">
                                        {t("dateOfBirth")}{" "}
                                        <span className="text-danger">
                                            *
                                        </span>{" "}
                                    </label>
                                    <DatePickerComponent
                                        type="Infant"
                                        index={index}
                                        control={control}
                                    />
                                </Col>
                                <Col md={6}>
                                    <label className="text-sm text-gray-600">
                                        {t("nationality")}{" "}
                                        <span className="text-danger">
                                            *
                                        </span>{" "}
                                    </label>
                                    <Controller
                                        name={`CountryInfant${index}`}
                                        control={control}
                                        rules={{
                                            required: t("countryIsRequired"),
                                        }}
                                        defaultValue={nationalityOptions.find(
                                            (option) => option.id === 213,
                                        )}
                                        render={({
                                            field,
                                            fieldState: { error },
                                        }) => (
                                            <>
                                                <SimpleSelect
                                                    options={countries}
                                                    placeholder={t(
                                                        "selectCountry",
                                                    )}
                                                    onChange={(x) => {
                                                        field.onChange(x);
                                                        setCountries(
                                                            nationalityOptions,
                                                        );
                                                    }}
                                                    defaultValue={nationalityOptions.find(
                                                        (option) =>
                                                            option.id === 213,
                                                    )}
                                                    onInputChange={
                                                        handleCountryChange
                                                    }
                                                    dashed={true}
                                                    clearable={true}
                                                />
                                                {error && (
                                                    <div className="text-red-500 text-[12px]">
                                                        {error.message}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>
                                {availabilityResults?.isDomesticFlight ? (
                                    <>
                                        <Col md={6}>
                                            <label className="text-sm text-gray-600">
                                                {t("identityCard")}
                                            </label>
                                            <Controller
                                                name={`identitiyInfant${index}`}
                                                control={control}
                                                render={({
                                                    field,
                                                    fieldState: { error },
                                                }) => (
                                                    <>
                                                        <SimpleSelect
                                                            searchable={false}
                                                            options={[
                                                                {
                                                                    value: t(
                                                                        "passport",
                                                                    ),
                                                                    label: t(
                                                                        "passport",
                                                                    ),
                                                                    id: 1,
                                                                },
                                                                {
                                                                    value: t(
                                                                        "identityCard",
                                                                    ),
                                                                    label: t(
                                                                        "identityCard",
                                                                    ),
                                                                    id: 2,
                                                                },
                                                            ]}
                                                            placeholder={t(
                                                                "selectIdentity",
                                                            )}
                                                            onChange={(x) =>
                                                                field.onChange(
                                                                    x,
                                                                )
                                                            }
                                                            dashed={true}
                                                        />
                                                        {error && (
                                                            <div className="text-red-500 text-[12px]">
                                                                {error.message}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <label className="text-sm text-gray-600">
                                                {t("cardNumber")}
                                            </label>
                                            <TextFormInput
                                                rules={{
                                                    pattern: {
                                                        value: /^[0-9]*$/,
                                                        message:
                                                            t(
                                                                "invalidCardNumber",
                                                            ),
                                                    },
                                                    validate: (value) => {
                                                        if (value?.length < 8)
                                                            return "card number must be at least 3 characters";
                                                        if (value?.length > 20)
                                                            return `card number must be less than 20 characters`;
                                                        return true;
                                                    },
                                                }}
                                                control={control}
                                                name={`cardNumberInfant${index}`}
                                                placeholder={t(
                                                    "enterCardNumber",
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <label className="text-sm text-gray-600">
                                                {t("cardExpiry")}
                                            </label>
                                            <Controller
                                                name={`cardExpiryInfant${index}`}
                                                control={control}
                                                render={({
                                                    field,
                                                }) => (
                                                    <>
                                                        <DatePicker
                                                            className="form-control flatpickr"
                                                            placeholder={t(
                                                                "enterPassportExpiry",
                                                            )}
                                                            options={{
                                                                altInput: true,
                                                                altFormat:
                                                                    "d M Y",
                                                                dateFormat:
                                                                    "Y-m-d",
                                                                minDate:
                                                                    getMinDateToExp(),
                                                            }}
                                                            onChange={(date) =>
                                                                field.onChange(
                                                                    date,
                                                                )
                                                            }
                                                            value={field.value}
                                                        />
                                                    </>
                                                )}
                                            />
                                        </Col>
                                    </>
                                ) : (
                                    <>
                                        <Col md={6}>
                                            <label className="text-sm text-gray-600">
                                                {t("passportNumber")}{" "}
                                                <span className="text-danger">
                                                    *
                                                </span>
                                            </label>
                                            <TextFormInput
                                                rules={{
                                                    required: t(
                                                        "passportNumberIsRequired",
                                                    ),
                                                    pattern: {
                                                        value: /^[0-9]*$/,
                                                        message: t(
                                                            "invalidPassportNumber",
                                                        ),
                                                    },
                                                    validate: (value) => {
                                                        if (value?.length !== 9)
                                                            return "passport number must be 9 numbers";
                                                        return true;
                                                    },
                                                }}
                                                control={control}
                                                name={`passportNumberInfant${index}`}
                                                placeholder={t(
                                                    "enterCardNumber",
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <label className="text-sm text-gray-600">
                                                {t("passportExpiry")}{" "}
                                                <span className="text-danger">
                                                    *
                                                </span>
                                            </label>
                                            <Controller
                                                name={`passportExpiryInfant${index}`}
                                                control={control}
                                                rules={{
                                                    required: t(
                                                        "passportExpiryIsRequired",
                                                    ),
                                                }}
                                                render={({
                                                    field,
                                                    fieldState: { error },
                                                }) => (
                                                    <>
                                                        <DatePicker
                                                            className="form-control flatpickr"
                                                            placeholder="Enter passport expiry"
                                                            options={{
                                                                altInput: true,
                                                                altFormat:
                                                                    "d M Y",
                                                                dateFormat:
                                                                    "Y-m-d",
                                                                minDate:
                                                                    getMinDateToExp(),
                                                            }}
                                                            onChange={(date) =>
                                                                field.onChange(
                                                                    date,
                                                                )
                                                            }
                                                            value={field.value}
                                                        />

                                                        <div className="text-red-500 text-[12px]">
                                                            {error &&
                                                                error.message}
                                                        </div>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                    </>
                                )}

                                {airValidateResults?.flight
                                    .flightNeedExchangeDocuments && (
                                    <>
                                        <FileUploadComponent
                                            control={control}
                                            type="Infant"
                                            index={index}
                                            fieldName={`exchangeDocumentsInfant${index}`}
                                        />
                                    </>
                                )}
                            </Row>
                        </>
                    )}
                </div>
            </AccordionBody>
        </AccordionItem>
    );
};

const FileUploadComponent = ({ control, fieldName }) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);

    const handleFileChange = (event, onChange) => {
        const selectedFiles = Array.from(event.target.files);
        if (files.length + selectedFiles.length > 2) {
            alert(t("maxFilesExceeded"));
            return;
        }
        const newFiles = [];
        selectedFiles.forEach((file) => {
            if (
                !file.type.startsWith("image/") &&
                file.type !== "application/pdf"
            ) {
                alert(t("invalidFileType"));
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                alert(t("fileTooLarge"));
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                file["id"] = Math.floor(Math.random() * 1000000 + 1);
                file["result"] = e.target.result;
                console.log(file);
                newFiles.push(file);

                if (newFiles.length === selectedFiles.length) {
                    const updatedFiles = [...files, ...newFiles];
                    setFiles(updatedFiles);
                    onChange(updatedFiles);
                }
            };
            reader.readAsDataURL(file);
        });
    };

    const removeFile = (id, onChange) => {
        const updatedFiles = files.filter((file) => file.id !== id);
        setFiles(updatedFiles);
        onChange(updatedFiles);
        if (updatedFiles.length === 0 && fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    useEffect(() => {
        console.log("files", files);
    }, [files]);

    return (
        <div className="col-lg-12 mt-3">
            <label className="text-sm text-gray-600">
                {t("uploadDocuments")} (PDF, Images)
                <span className="text-danger">*</span>
            </label>

            <Controller
                name={fieldName}
                control={control}
                rules={{
                    required: t("requiredDocuments"),
                    validate: (value) => {
                        if (!value || value.length === 0) {
                            return t("atLeastOneFileRequired");
                        }
                        if (value.length > 2) {
                            return t("maxFilesExceeded");
                        }
                        return true;
                    },
                }}
                render={({ field, fieldState: { error } }) => (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="d-none"
                            multiple
                            accept="image/*,application/pdf"
                            onChange={(e) =>
                                handleFileChange(e, field.onChange)
                            }
                        />

                        <button
                            type="button"
                            className="btn btn-outline-secondary w-100 text-left"
                            onClick={triggerFileInput}
                        >
                            <i className="fa fa-cloud-upload me-2"></i>
                            {t("chooseFiles")} ({files.length}/2)
                        </button>

                        <div className=" flex gap-3 mt-3 w-fit">
                            {files.map((file) => (
                                <div
                                    key={file.id}
                                    className="d-flex align-items-center gap-3 mb-2 p-2 border rounded"
                                    style={{ position: "relative" }}
                                >
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() =>
                                            removeFile(file.id, field.onChange)
                                        }
                                        style={{
                                            position: "absolute",
                                            top: "5px",
                                            right: "5px",
                                            zIndex: 1,
                                        }}
                                    >
                                        <i className="fa fa-times"></i>
                                    </button>

                                    {file.type.startsWith("image/") ? (
                                        <img
                                            src={file.result}
                                            alt={file.name}
                                            style={{
                                                height: "150px",
                                                width: "150px",
                                                objectFit: "cover",
                                                borderRadius: "4px",
                                            }}
                                        />
                                    ) : (
                                        <div className="position-relative">
                                            <iframe
                                                src={file.result}
                                                title={file.name}
                                                width="200"
                                                height="200"
                                                style={{
                                                    border: "1px solid #ddd",
                                                    borderRadius: "4px",
                                                }}
                                            />
                                            <div className="text-center mt-1">
                                                <small className="text-muted">
                                                    {file.name}
                                                </small>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex-grow-1 self-end">
                                        <div className="text-sm font-medium">
                                            {file.name}
                                        </div>
                                        <div className="text-xs text-muted">
                                            {(file.size / 1024 / 1024).toFixed(
                                                2,
                                            )}{" "}
                                            MB
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {error && (
                            <div className="text-red-500 text-[12px] mt-1">
                                {error.message}
                            </div>
                        )}

                        <div className="text-muted text-xs mt-1">
                            {t("maxFilesHint")} (2 {t("filesMax")})
                        </div>
                    </>
                )}
            />
        </div>
    );
};

const ageRanges = {
    adult: { minYears: 12, maxYears: 100 },
    child: { minYears: 2, maxYears: 12 },
    infant: { minYears: 0, maxYears: 2 },
    senior: { minYears: 60, maxYears: 100 },
};

const getDateRange = (type) => {
    const today = new Date();
    const { minYears, maxYears } = ageRanges[type] || {};
    const minDate = new Date(
        today.getFullYear() - maxYears,
        today.getMonth(),
        today.getDate(),
    );
    const maxDate = new Date(
        today.getFullYear() - minYears,
        today.getMonth(),
        today.getDate(),
    );
    return { minDate, maxDate };
};

const DatePickerComponent = ({ type, index, control }) => {
    const { minDate, maxDate } = getDateRange(type.toLocaleLowerCase());
    const { t } = useTranslation();
    return (
        <Controller
            name={`dob${type}${index}`}
            control={control}
            rules={{
                required: t("dateOfBirthIsRequired"),
            }}
            render={({ field, fieldState: { error } }) => (
                <>
                    <DatePicker
                        className="form-control flatpickr"
                        placeholder={t("enterDateOfBirth")}
                        options={{
                            altInput: true,
                            altFormat: "d M Y",
                            dateFormat: "Y-m-d",
                            maxDate: maxDate,
                            minDate: minDate,
                            yearSelectorType: "dropdown",
                        }}
                        onChange={(date) => field.onChange(date)}
                        value={field.value}
                    />
                    {error && (
                        <p className="text-red-500 text-[12px]">
                            {error.message}
                        </p>
                    )}
                </>
            )}
        />
    );
};
